/**
 * The two confirmations around a per-account mute (#1892).
 *
 * Both exist because the decided semantics are counter-intuitive in BOTH
 * directions, and each dialog states the half an operator is about to be
 * surprised by:
 *
 *   - MUTING destroys the account's alert state. `first_alerted_at` — how long
 *     this customer has been down, the number an operator judges severity by —
 *     is gone from the dedup table the moment the button lands. It survives in
 *     the send history, which is exactly why that table had to ship first.
 *   - RESTORING is not a no-op. An account still over the line receives a
 *     FRESH alert on the next pass, because as far as they know nothing was
 *     ever announced. An operator expecting "unmute = back to how it was"
 *     would be scheduling a mail without realising it.
 */
import type { ReactNode } from 'react';
import { useT } from '../../i18n/index.ts';
import { ConfirmDialog } from '../../ui/index.ts';

interface SuppressProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly email: string;
  readonly loading: boolean;
  readonly reasonField: ReactNode;
  readonly onConfirm: () => void;
}

export function BalanceAlertSuppressDialog({
  open,
  onOpenChange,
  email,
  loading,
  reasonField,
  onConfirm,
}: SuppressProps): JSX.Element {
  const t = useT();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      tone="danger"
      title={t('balance_alerts.suppress.confirm_title', { email })}
      body={
        <div className="flex flex-col gap-3">
          <p
            className="m-0 rounded border border-bad-line bg-bad-dim/50 px-2.5 py-2 text-dense text-bad-bright"
            data-testid="balance-alerts-suppress-confirm-consequence"
          >
            {t('balance_alerts.suppress.confirm_consequence')}
          </p>
          {/* Second, deliberately: read first, "they will get a new alert when
              you lift this" sounds like a reason not to worry. */}
          <p className="m-0">{t('balance_alerts.suppress.confirm_not_permanent')}</p>
          {reasonField}
        </div>
      }
      confirmLabel={t('balance_alerts.suppress.confirm_action')}
      cancelLabel={t('common.cancel')}
      closeLabel={t('common.close')}
      loading={loading}
      onConfirm={onConfirm}
      testId="balance-alerts-suppress-confirm"
    />
  );
}

interface RestoreProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly loading: boolean;
  readonly onConfirm: () => void;
}

export function BalanceAlertRestoreDialog({
  open,
  onOpenChange,
  loading,
  onConfirm,
}: RestoreProps): JSX.Element {
  const t = useT();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('balance_alerts.restore.confirm_title')}
      body={
        <div className="flex flex-col gap-3">
          <p
            className="m-0 rounded border border-warn-line bg-warn-dim/50 px-2.5 py-2 text-dense text-warn"
            data-testid="balance-alerts-restore-confirm-consequence"
          >
            {t('balance_alerts.restore.confirm_consequence')}
          </p>
          <p className="m-0">{t('balance_alerts.restore.confirm_detail')}</p>
        </div>
      }
      confirmLabel={t('balance_alerts.restore.confirm_action')}
      cancelLabel={t('common.cancel')}
      closeLabel={t('common.close')}
      loading={loading}
      onConfirm={onConfirm}
      testId="balance-alerts-restore-confirm"
    />
  );
}
